/**
 * Postmark Suppressions Tile — Feb 2026
 * ======================================
 *
 * Surfaces the most-recent Postmark hard-bounces, spam complaints, and
 * manual unsubscribes for the Command Centre. Pairs with the
 * suppression-sync pipeline so the founder sees deliverability problems
 * within minutes instead of finding out the next broadcast cycle.
 *
 * Renders:
 *   • Header strip with total suppressions + reason breakdown chips
 *   • One-click "Sync to local DB" button (idempotent)
 *   • Scrollable table — last N entries with email + reason + when +
 *     a tiny pill marking which entries were *our* subscribers
 *
 * Brand: matches NewsletterOperationsCard (purple #7B4FD8 / orange #F5831F).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, RefreshCw, Loader2, CheckCircle, AlertTriangle, Mail } from 'lucide-react';
import { apiClient } from '../../api/client';

const BRAND_PURPLE = '#7B4FD8';
const BRAND_ORANGE = '#F5831F';
const BRAND_INK = '#1A1209';

const REASON_TONE = {
  HardBounce:        { bg: '#FEE2E2', fg: '#991B1B', label: 'Hard bounce' },
  SpamComplaint:     { bg: '#FEF3C7', fg: '#92400E', label: 'Spam complaint' },
  ManualSuppression: { bg: '#E0E7FF', fg: '#3730A3', label: 'Manual unsub' },
  Unsubscribe:       { bg: '#F3E8FF', fg: '#6B21A8', label: 'Unsubscribed' },
  Unknown:           { bg: '#F3F4F6', fg: '#374151', label: 'Other' },
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16); }
};

const ReasonChip = ({ reason, count }) => {
  const tone = REASON_TONE[reason] || REASON_TONE.Unknown;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
      data-testid={`suppression-reason-chip-${reason}`}
    >
      {tone.label}
      <span className="font-bold">{count}</span>
    </span>
  );
};

const RowPillLocal = () => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md"
    style={{ backgroundColor: '#F5831F22', color: BRAND_ORANGE }}
    title="This address is in your local newsletter_subscribers collection"
  >
    Local
  </span>
);

const PostmarkSuppressionsTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.request('/api/admin/newsletter/suppressions/recent?limit=25');
      setData(res);
    } catch (e) {
      setError(e?.message || 'Failed to load suppressions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const res = await apiClient.request('/api/admin/newsletter/sync-suppressions', {
        method: 'POST',
      });
      setSyncResult(res);
      // Re-fetch the tile so the "Local" pills reflect freshly-stamped rows.
      await load();
    } catch (e) {
      setError(e?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const breakdown = data?.reason_breakdown ?? {};

  return (
    <div
      className="rounded-2xl border-2 shadow-sm bg-white"
      style={{ borderColor: '#EDE7F8' }}
      data-testid="postmark-suppressions-tile"
    >
      {/* Header */}
      <div className="p-5 border-b" style={{ borderColor: '#F3F0FB' }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div
              className="rounded-xl p-2.5"
              style={{ backgroundColor: BRAND_PURPLE + '14', color: BRAND_PURPLE }}
            >
              <ShieldAlert size={22} />
            </div>
            <div>
              <h3 className="text-base font-semibold" style={{ color: BRAND_INK }}>
                Deliverability — Postmark Suppressions
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Live view of bounces, spam complaints &amp; unsubscribes from Postmark.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={runSync}
            disabled={syncing || loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-50 transition-colors"
            style={{ backgroundColor: BRAND_PURPLE }}
            data-testid="suppressions-sync-btn"
            title="Mirror Postmark's authoritative list into newsletter_subscribers (idempotent)"
          >
            {syncing
              ? <><Loader2 size={15} className="animate-spin" /> Syncing…</>
              : <><RefreshCw size={15} /> Sync to local DB</>}
          </button>
        </div>

        {/* Totals strip */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <div
            className="px-3 py-1.5 rounded-lg text-sm font-bold"
            style={{ backgroundColor: BRAND_INK + '08', color: BRAND_INK }}
            data-testid="suppressions-total"
          >
            {loading ? '…' : total} total
          </div>
          {Object.entries(breakdown).map(([reason, count]) => (
            <ReasonChip key={reason} reason={reason} count={count} />
          ))}
        </div>

        {/* Sync result banner */}
        {syncResult && (
          <div
            className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
            style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}
            data-testid="suppressions-sync-result"
          >
            <CheckCircle size={16} />
            <span>
              Sync complete: <b>{syncResult.fetched}</b> fetched ·
              <b className="ml-1">{syncResult.newly_unsubscribed}</b> newly marked ·
              <b className="ml-1">{syncResult.already_unsubscribed}</b> already marked ·
              <b className="ml-1">{syncResult.not_in_local_db}</b> not in our list
            </span>
          </div>
        )}

        {error && (
          <div
            className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
            style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
            data-testid="suppressions-error"
          >
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-5">
        {loading && !data ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 size={20} className="animate-spin mr-2" />
            Loading suppressions…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Mail size={28} className="text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">
              No suppressions — your sender reputation is clean.
            </p>
          </div>
        ) : (
          <div
            className="overflow-x-auto rounded-lg border"
            style={{ borderColor: '#F3F0FB', maxHeight: 380 }}
            data-testid="suppressions-table-wrap"
          >
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ backgroundColor: '#FAF8FE' }}>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Email</th>
                  <th className="px-3 py-2 font-semibold">Reason</th>
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Origin</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const tone = REASON_TONE[r.reason] || REASON_TONE.Unknown;
                  return (
                    <tr
                      key={`${r.email}-${idx}`}
                      className="border-t hover:bg-gray-50/60"
                      style={{ borderColor: '#F3F0FB' }}
                      data-testid={`suppression-row-${idx}`}
                    >
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate" style={{ color: BRAND_INK }}>{r.email}</span>
                          {r.is_local_subscriber && <RowPillLocal />}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <span
                          className="inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full"
                          style={{ backgroundColor: tone.bg, color: tone.fg }}
                        >
                          {tone.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle text-gray-600 whitespace-nowrap">
                        {fmtWhen(r.created_at)}
                      </td>
                      <td className="px-3 py-2 align-middle text-gray-500 text-xs">
                        {r.origin}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostmarkSuppressionsTile;
